import { cn, Image, Tooltip } from '@heroui/react';
import { useMemo } from 'react';

import {
  resolveTriggerTooltipName,
  type TriggerTooltipPipelineLike
} from './resolveTriggerTooltipName';

import i18n from '~/common/helpers/i18n';
import images from '~/common/jsons/images.json';
import {
  getTriggerCategoryIcon,
  type TriggerCategory
} from '~/common/utils/triggerCategoryIcons';

type TriggerIconProps = {
  triggerSpec?: TriggerTooltipPipelineLike['triggerSpec'];
  triggerCategory?: TriggerCategory;
  iconSrc?: string;
  // BACKEND-API-TRIGGER: remove prop; tooltip uses triggerSpec.name once backend sends `api`.
  pipeline?: TriggerTooltipPipelineLike;
};

export function TriggerIcon({
  triggerSpec,
  triggerCategory,
  iconSrc,
  pipeline
}: TriggerIconProps) {
  const IconComponent = getTriggerCategoryIcon(triggerCategory);

  const tooltipPipeline = useMemo((): TriggerTooltipPipelineLike => {
    if (pipeline) {
      return pipeline;
    }
    return { triggerSpec };
  }, [pipeline, triggerSpec]);

  const triggerTooltipName = useMemo(
    () => resolveTriggerTooltipName(tooltipPipeline),
    [tooltipPipeline]
  );

  return (
    <Tooltip
      content={
        <div className='flex flex-col'>
          <span className='text-label-md text-on-surface-variant'>
            {triggerTooltipName
              ? triggerCategory
              : i18n.t('scenes.pipelines.labels.no_trigger')}
          </span>
          {triggerTooltipName && (
            <span className='text-overline-regular uppercase text-on-surface'>
              {triggerTooltipName}
            </span>
          )}
        </div>
      }
      placement='right'
      classNames={{
        content: 'p-2'
      }}
    >
      <div className='relative flex h-10 w-10 items-center justify-center rounded-md bg-surface-dim'>
        <Image
          alt={triggerTooltipName || 'Generic trigger'}
          className={cn('h-full w-full', !iconSrc ? 'bg-[#7a78b8]' : '')}
          src={iconSrc ?? `data:image/svg+xml;base64,${images.trigger}`}
        />
        <div className='pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border-1 border-solid border-outline bg-surface text-on-surface-variant shadow-md'>
          {IconComponent && <IconComponent className='h-3 w-3' />}
        </div>
      </div>
    </Tooltip>
  );
}
